import React from 'react';
import { ContextArrowBox } from '@mergermarket/acuris-react-arrow-box';
import DisplayOnBreakpoint from '@mergermarket/acuris-react-display-on-breakpoint';
import { useFeatureFlag } from '@mergermarket/acuris-react-feature-flags';
import { FocusContextProvider } from '@mergermarket/acuris-react-focus-context';
import Search from '@mergermarket/acuris-react-universal-search';
import { UserMenuButton } from '@mergermarket/acuris-react-user-menu';
import { productOrEditionHomepageUrls, isEntitledOnlyToAbsMuniPrimaryIssuanceOrXtract, editionsForIdArray } from '@mergermarket/editions-lookup';
import { Header, HeaderTop, HeaderLogo, ActionList, ActionItem, Navigation, NavigationBar, NavigationList, NavigationItem, SecondaryNavigation, NavigationMenu } from '@mergermarket/acuris-react-header';
import DebtwireLogo from '@mergermarket/debtwire-react-logo';

const ENV = Object.freeze({
  CI: 'ci',
  ASLIVE: 'aslive',
  LIVE: 'live'
});
const homepage = {
  link: '/homepage',
  position: 5,
  displayName: 'Home'
};
const companyDirectory = {
  link: '/companies',
  position: 20,
  displayName: 'Company Directory'
};
const xtractResearch = {
  link: 'https://www.xtractresearch.com',
  displayName: 'Xtract Research',
  position: 60
};
const creditFluxLive = {
  link: 'https://www.creditflux.com',
  displayName: 'Creditflux',
  position: 70
};
const creditFluxAslive = {
  link: 'https://aslive-www.creditflux.com',
  displayName: 'Creditflux',
  position: 70
};
const creditFluxCi = {
  link: 'https://ci-www.creditflux.com',
  displayName: 'Creditflux',
  position: 70
};
const cloiCi = {
  link: 'https://ci-cloi.creditflux.com',
  displayName: 'CLO-i',
  position: 72
};
const cloiAslive = {
  link: 'https://aslive-cloi.creditflux.com',
  displayName: 'CLO-i',
  position: 72
};
const cloiLive = {
  link: 'https://cloi.creditflux.com',
  displayName: 'CLO-i',
  position: 72
};
const fundsData = {
  link: '/funds-data',
  displayName: 'Funds Data',
  position: 75
};
const primaryIssuance = {
  hideOnSmallViewports: true,
  displayName: 'Primary ID',
  link: '/primary-issuance',
  position: 80
};
const restructuringDb = {
  id: '8.2048',
  displayName: 'Restructuring Data',
  link: '/restructuringdb',
  position: 90,
  hideOnSmallViewports: true
};
const dockets = {
  link: '/dockets',
  displayName: 'Dockets',
  position: 100
};
const creditRubricLive = {
  link: 'https://www.creditrubric.com/company',
  displayName: 'Credit Rubric',
  position: 105
};
const creditRubricAslive = {
  link: 'https://aslive-www.creditrubric.com/company',
  displayName: 'Credit Rubric',
  position: 105
};
const creditRubricCi = {
  link: 'https://ci-www.creditrubric.com/company',
  displayName: 'Credit Rubric',
  position: 105
};
const topics = {
  link: '/topics',
  displayName: 'Topics',
  position: 110
};

const addDisplayName = item => Object.assign({
  displayName: item.name
}, item);

const hideOnSmallViewports = item => ({ ...item,
  hideOnSmallViewports: true
});

const removePositionProperty = ({
  position,
  ...navLink
}) => navLink;

const getNavigationLinks = (editionsUserIsEntitledTo, env, subscriptionTags) => {
  const legacyEditions = productOrEditionHomepageUrls(editionsUserIsEntitledTo, env, subscriptionTags).map(hideOnSmallViewports).map(addDisplayName);
  const shouldSeeCompanyDirectory = !isEntitledOnlyToAbsMuniPrimaryIssuanceOrXtract(editionsUserIsEntitledTo);

  const creditRubric = environment => {
    if (environment === ENV.CI) {
      return creditRubricCi;
    }

    if (environment === ENV.ASLIVE) {
      return creditRubricAslive;
    }

    return creditRubricLive;
  };

  const cloi = environment => {
    if (environment === ENV.CI) {
      return cloiCi;
    }

    if (environment === ENV.ASLIVE) {
      return cloiAslive;
    }

    return cloiLive;
  };

  const creditFlux = environment => {
    if (environment === ENV.CI) {
      return creditFluxCi;
    }

    if (environment === ENV.ASLIVE) {
      return creditFluxAslive;
    }

    return creditFluxLive;
  };

  const links = [...legacyEditions, homepage, topics, primaryIssuance, restructuringDb, dockets, xtractResearch, creditFlux(env.toLowerCase()), fundsData, cloi(env.toLowerCase()), creditRubric(env.toLowerCase())].concat(shouldSeeCompanyDirectory ? companyDirectory : []).sort((navLinkA, navLinkB) => navLinkA.position - navLinkB.position).map(removePositionProperty);
  return links;
};

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

const buildActiveLinkTagger = activeRoute => edition => edition.link === activeRoute ? { ...edition,
  active: true
} : edition;

const linkPrepender = dwDomain => link => {
  if (typeof dwDomain === 'undefined' || dwDomain === '' || link.match('//')) {
    return link;
  }

  const prefix = link[0] === '/' && dwDomain.slice(-1) === '/' ? dwDomain.slice(0, -1) : dwDomain;
  return `${prefix}${link}`;
};

const renderActionItems = actionItems => /*#__PURE__*/React.createElement(ActionList, null, actionItems.map(item => {
  if (item.type.hideOnSmallViewports) {
    return /*#__PURE__*/React.createElement(DisplayOnBreakpoint, {
      useNewBreakpoints: true,
      key: item.key || undefined,
      greaterThan: "large"
    }, /*#__PURE__*/React.createElement(ActionItem, null, item));
  }

  return /*#__PURE__*/React.createElement(ActionItem, {
    key: item.key || undefined
  }, item);
}));

const DebtwireHeader = ({
  actionItems = [],
  activeRoute,
  dwDomain,
  entitlements = [],
  env,
  secondaryLinks = [],
  subscriptionTags = [],
  userMenuProps
}) => {
  const prepender = linkPrepender(dwDomain);

  const domainPrepender = navItem => ({ ...navItem,
    link: prepender(navItem.link)
  });

  const editionsUserIsEntitledTo = editionsForIdArray(entitlements).filter(x => x); // TODO figure out why we need to remove nulls and undefineds

  const navItems = getNavigationLinks(editionsUserIsEntitledTo, env, subscriptionTags);
  const navigationLinks = navItems.map(buildActiveLinkTagger(activeRoute)).map(domainPrepender);
  const secondaryNavigationLinks = secondaryLinks.map(domainPrepender);
  const homeLink = prepender('/homepage');
  return /*#__PURE__*/React.createElement(FocusContextProvider, null, /*#__PURE__*/React.createElement(Header, null, /*#__PURE__*/React.createElement(HeaderTop, null, /*#__PURE__*/React.createElement(HeaderLogo, {
    homeURL: homeLink
  }, /*#__PURE__*/React.createElement(DebtwireLogo, null)), useFeatureFlag('newSearchComponent') ? /*#__PURE__*/React.createElement(ActionList, null, /*#__PURE__*/React.createElement(ActionItem, null, /*#__PURE__*/React.createElement(Search, {
    id: "dwsearch",
    searchProps: {}
  })), /*#__PURE__*/React.createElement(ActionItem, null, /*#__PURE__*/React.createElement(UserMenuButton, {
    userMenuProps: userMenuProps
  }))) : actionItems && renderActionItems(actionItems)), /*#__PURE__*/React.createElement(ContextArrowBox, null), /*#__PURE__*/React.createElement(Navigation, null, /*#__PURE__*/React.createElement(NavigationBar, null, /*#__PURE__*/React.createElement(NavigationList, {
    primary: true
  }, navigationLinks.map(item => /*#__PURE__*/React.createElement(NavigationItem, _extends({}, item, {
    key: item.link
  })))), secondaryNavigationLinks && secondaryNavigationLinks.length > 0 && /*#__PURE__*/React.createElement(SecondaryNavigation, null, /*#__PURE__*/React.createElement(NavigationList, {
    secondary: true
  }, secondaryNavigationLinks.map(item => /*#__PURE__*/React.createElement(NavigationItem, _extends({}, item, {
    key: item.link
  })))))), /*#__PURE__*/React.createElement(NavigationMenu, null, /*#__PURE__*/React.createElement(NavigationList, {
    primary: true
  }, navigationLinks.map(item => /*#__PURE__*/React.createElement(NavigationItem, _extends({}, item, {
    key: item.link
  }))))))));
};

export default DebtwireHeader;
//# sourceMappingURL=index.js.map
